import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import ServiceBlock from './components/ServiceBlock';
import ServicesFAQ from './components/ServicesFAQ';
import Process from '../home/components/Process';
import About from '../home/components/About';
import Testimonials from '../home/components/Testimonials';
import ServiceArea from '../home/components/ServiceArea';
import CTASection from '../home/components/CTASection';
import Gallery from '../home/components/Gallery';
import ConsultationForm from '../home/components/ConsultationForm';

const services = [
  {
    id: 'custom-suits',
    icon: 'ri-shirt-line',
    tagline: 'The Pinnacle of Tailoring',
    title: 'Custom Bespoke Suits',
    description: 'From boardroom to black tie, our master tailors craft suits that fit like a second skin. Every seam, every lapel, and every button reflects your exact proportions and personal style. No off-the-rack shortcut — only pure bespoke craftsmanship built from scratch.',
    features: [
      'Over 500 premium fabrics from Loro Piana, Holland & Sherry, Scabal, and Dormeuil',
      'Full canvas, half canvas, or fused construction — your choice',
      'Single-breasted, double-breasted, notch lapel, peak lapel, shawl collar',
      'Personalized lining, buttons, monogram, and hand-finished details',
      'Multiple in-person fittings included until the fit is perfect',
      'Suits for business, weddings, events, and everyday wear',
    ],
    image: 'https://public.readdy.ai/ai/img_res/1ab4ca197003e2454d3a71d8c3ef0e63.jpg',
    imageAlt: 'Custom bespoke suit by British Custom Tailors',
    reverse: false,
  },
  {
    id: 'custom-shirts',
    icon: 'ri-t-shirt-line',
    tagline: 'Handcrafted to Your Measurements',
    title: 'Custom Dress Shirts',
    description: 'A perfectly fitted shirt changes everything. Our handcrafted dress shirts are built from your exact measurements — chosen collar style, cuff design, and fabric weight — so every detail suits your lifestyle and aesthetic.',
    features: [
      'Egyptian cotton, Sea Island cotton, Poplin, Twill, Oxford, and luxury linen',
      'Spread collar, button-down, cutaway, tab collar — any style you prefer',
      'French cuffs, barrel cuffs, cocktail cuffs with custom cufflink options',
      'Custom monogramming on cuff, breast pocket, or collar',
      'Dress shirts, casual shirts, business and formal styles available',
      'Perfect sleeve length, neck size, and body fit guaranteed',
    ],
    image: 'https://readdy.ai/api/search-image?query=luxury%20custom%20dress%20shirts%20neatly%20arranged%20on%20wooden%20shelves%20in%20a%20high%20end%20tailoring%20showroom%2C%20crisp%20white%20and%20pale%20blue%20fabrics%2C%20subtle%20stitching%20detail%20visible%2C%20soft%20natural%20light%2C%20clean%20minimalist%20background%2C%20premium%20menswear%20atmosphere&width=700&height=500&seq=srv2-shirts-02&orientation=landscape',
    imageAlt: 'Custom dress shirts tailored to your measurements',
    reverse: true,
  },
  {
    id: 'alterations',
    icon: 'ri-scissors-line',
    tagline: 'Precision Alterations',
    title: 'Expert Tailoring & Alterations',
    description: 'Your existing wardrobe deserves a perfect fit. Our master alterations team handles everything from quick trouser hems to complete suit reconstructions. We work on every type of garment, every fabric, and every level of complexity — with the precision only decades of experience can deliver.',
    features: [
      'Suit and blazer alterations — taking in, letting out, reshaping',
      'Trouser alterations — hemming, waist adjustment, taper and seat',
      'Dress and skirt alterations for formal and casual wear',
      'Shirt alterations — collar, sleeve, body and cuff adjustments',
      'Same-day and rush alterations available for urgent needs',
      'Work on designer garments and luxury fabrics handled with care',
    ],
    image: 'https://readdy.ai/api/search-image?query=professional%20master%20tailor%20carefully%20hand-stitching%20a%20fine%20suit%20jacket%20in%20a%20traditional%20tailoring%20workshop%2C%20warm%20focused%20lighting%20on%20skilled%20hands%2C%20measuring%20tape%20draped%20over%20shoulder%2C%20clean%20neutral%20workshop%20background%2C%20artisan%20craftsmanship%20atmosphere&width=700&height=500&seq=srv2-alter-03&orientation=landscape',
    imageAlt: 'Expert tailoring and alterations by British Custom Tailors',
    reverse: false,
  },
  {
    id: 'wedding-formal',
    icon: 'ri-vip-line',
    tagline: 'Your Most Important Day',
    title: 'Wedding & Formal Wear',
    description: 'Your wedding day, your gala, your defining moment — deserves a garment crafted entirely for you. We specialize in bespoke wedding suits, morning suits, tuxedos, and complete formal ensembles for the groom, groomsmen, and every member of your party.',
    features: [
      'Bespoke morning suits, lounge suits, and black tie tuxedos',
      'Full bridal party packages — groom, groomsmen, and ushers',
      'Wide selection of formal fabrics in classic and contemporary styles',
      'Coordinating accessories including pocket squares, ties, and waistcoats',
      'Multiple fitting sessions scheduled well in advance of your event',
      'Recommend booking 8 to 12 weeks before the event for full bespoke service',
    ],
    image: 'https://readdy.ai/api/search-image?query=elegant%20wedding%20suit%20and%20formal%20wear%20arranged%20in%20a%20luxury%20tailoring%20studio%2C%20crisp%20white%20dress%20shirt%20with%20black%20tuxedo%2C%20boutonniere%20detail%2C%20warm%20golden%20light%2C%20fine%20fabric%20texture%20visible%2C%20ceremonial%20and%20sophisticated%20atmosphere&width=700&height=500&seq=srv2-wedding-04&orientation=landscape',
    imageAlt: 'Bespoke wedding and formal wear tailored by British Custom Tailors',
    reverse: true,
  },
  {
    id: 'house-calls',
    icon: 'ri-home-office-line',
    tagline: 'We Come to You — Anywhere',
    title: 'House & Office Call Service',
    description: 'Why travel when the tailor comes to you? Our nationwide house call and office call service brings the complete British Custom Tailors studio experience directly to your location — with fabric samples, measuring tools, and expert consultation.',
    features: [
      'Available nationwide across all 50 states',
      'Full consultation with our expert traveling consultants',
      'Brings 200+ fabric swatches and complete style guides',
      'Ideal for executives, busy professionals, and those who value privacy',
      'Group and corporate packages for teams and offices',
      'Flexible scheduling — evenings and weekends available',
    ],
    image: 'https://readdy.ai/api/search-image?query=professional%20tailor%20consultant%20sitting%20with%20client%20at%20an%20elegant%20home%20office%2C%20fabric%20swatch%20books%20open%20on%20table%2C%20natural%20warm%20light%20through%20window%2C%20professional%20yet%20relaxed%20consultation%20setting%2C%20premium%20fabric%20samples%20visible%2C%20luxury%20home%20environment&width=700&height=500&seq=srv2-house-05&orientation=landscape',
    imageAlt: 'British Custom Tailors house call and office call service nationwide',
    reverse: false,
  },
  {
    id: 'style-consultation',
    icon: 'ri-palette-line',
    tagline: 'Build a Wardrobe That Works',
    title: 'Style Consultation',
    description: 'Not sure what suits your body type, lifestyle, or industry? Our style consultants provide expert, personalized guidance on fabrics, silhouettes, color palettes, and wardrobe strategies — helping you invest wisely in clothing that serves you for years.',
    features: [
      'Full wardrobe audit and gap analysis',
      'Body type and posture assessment for optimal silhouette',
      'Occasion-specific dressing strategy — business, formal, casual',
      'Color and fabric pairing tailored to your complexion and preferences',
      'Capsule wardrobe planning for executives and public figures',
      'Ongoing style advisory for returning clients',
    ],
    image: 'https://readdy.ai/api/search-image?query=elegant%20menswear%20style%20consultation%20session%20in%20a%20refined%20showroom%2C%20tailor%20presenting%20fabric%20swatches%20and%20suit%20options%20to%20a%20well-dressed%20client%2C%20warm%20refined%20interior%2C%20fabric%20books%20open%20on%20a%20glass%20table%2C%20premium%20atmosphere%20with%20natural%20lighting&width=700&height=500&seq=srv2-style-06&orientation=landscape',
    imageAlt: 'Expert style consultation by British Custom Tailors',
    reverse: true,
  },
];

const trustStats = [
  { icon: 'ri-history-line', value: '55+', label: 'Years of Excellence' },
  { icon: 'ri-user-heart-line', value: '5,000+', label: 'Satisfied Clients' },
  { icon: 'ri-tools-line', value: '500+', label: 'Fabric Options' },
  { icon: 'ri-map-2-line', value: '50', label: 'States Served' },
];

export default function ServicesPage() {
  const scrolled = true;

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} />

      {/* ── 1. HERO ── */}
      <section className="relative min-h-[580px] pt-32 flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=sophisticated%20British%20tailoring%20studio%20interior%20with%20rows%20of%20premium%20fabric%20bolts%20on%20dark%20wooden%20shelves%2C%20large%20cutting%20table%20in%20center%20with%20professional%20measuring%20tape%20and%20shears%2C%20warm%20amber%20and%20gold%20lighting%2C%20deep%20navy%20walls%20with%20framed%20fashion%20illustrations%2C%20Savile%20Row%20inspired%20luxury%20craftsmanship%20atmosphere&width=1440&height=580&seq=services-hero-fix-2026&orientation=landscape')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/50 to-black/65" />
        <div className="relative z-10 text-center w-full px-4 py-16">
          <p className="text-[#C9A961] text-xs font-bold tracking-widest uppercase mb-5">British Custom Tailors</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Tailoring Services <br /><span className="text-[#C9A961]">Built for You</span>
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto mb-10 leading-relaxed">
            From handcrafted custom suits to expert alterations and nationwide house calls — every service is designed to deliver an extraordinary fit and lasting quality.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:866-857-8487"
              className="inline-flex items-center justify-center bg-[#C9A961] text-white px-10 py-4 rounded-full text-base font-semibold hover:bg-[#B89851] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call 866-857-8487
            </a>
            <a
              href="#custom-suits"
              className="inline-flex items-center justify-center border border-white/40 text-white px-10 py-4 rounded-full text-base font-semibold hover:border-[#C9A961] hover:text-[#C9A961] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Explore Services
              <i className="ri-arrow-down-line ml-2"></i>
            </a>
          </div>
        </div>
      </section>

      {/* ── 2. TRUST BAR ── */}
      <section className="py-10 bg-gray-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {trustStats.map((stat, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#C9A961]/15 rounded-xl flex-shrink-0">
                  <i className={`${stat.icon} text-2xl text-[#C9A961]`}></i>
                </div>
                <div>
                  <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>{stat.value}</div>
                  <div className="text-xs text-gray-400 font-medium">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. SERVICES ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#C9A961] text-xs font-bold tracking-widest uppercase mb-3">What We Offer</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Every Service You <span className="text-[#C9A961]">Could Need</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive tailoring, crafted with exceptional care and delivered anywhere in the country
            </p>
          </div>
          <div className="space-y-28">
            {services.map((service) => (
              <ServiceBlock key={service.id} {...service} />
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. WHY CHOOSE US ── */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#C9A961] text-xs font-bold tracking-widest uppercase mb-4">Our Promise</p>
              <h2 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Why Our Clients <br />Never Go Anywhere Else
              </h2>
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                Since 1969, British Custom Tailors has built its reputation on one simple principle: every client deserves clothing that fits perfectly, is built to last, and reflects their individuality. That promise has never changed.
              </p>
              <div className="space-y-5">
                {[
                  { icon: 'ri-medal-line', title: 'Traditional British Craftsmanship', desc: 'Techniques passed down through generations — applied to every garment.' },
                  { icon: 'ri-vip-diamond-line', title: 'The World\'s Finest Fabrics', desc: 'Sourced exclusively from Loro Piana, Scabal, Holland & Sherry, and Dormeuil.' },
                  { icon: 'ri-shield-check-line', title: 'Satisfaction Guaranteed', desc: 'We don\'t stop refining until you\'re completely satisfied with every detail.' },
                  { icon: 'ri-global-line', title: 'Nationwide Personal Service', desc: 'We travel to you anywhere in the U.S. — house calls, office calls, and studio visits.' },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 items-start">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#C9A961]/20 rounded-lg flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-[#C9A961] text-lg`}></i>
                    </div>
                    <div>
                      <div className="font-bold text-white mb-1">{item.title}</div>
                      <div className="text-gray-400 text-sm leading-relaxed">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden h-[560px]">
              <img
                src="https://readdy.ai/api/search-image?query=master%20tailor%20in%20a%20traditional%20British%20tailoring%20studio%20closely%20inspecting%20a%20bespoke%20suit%20lapel%20with%20precision%20and%20care%2C%20warm%20workshop%20lighting%2C%20dark%20wood%20background%2C%20artisan%20tools%20visible%2C%20decades%20of%20expertise%20visible%20in%20steady%20confident%20hands&width=700&height=560&seq=srv2-trust-img&orientation=portrait"
                alt="Master tailor at British Custom Tailors"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 bg-black/60 backdrop-blur-sm rounded-xl p-5 border border-white/10">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#C9A961] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-award-fill text-xl text-white"></i>
                  </div>
                  <div>
                    <div className="text-white font-bold">Established 1969</div>
                    <div className="text-gray-300 text-sm">Over 55 years of bespoke tailoring excellence</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. PROCESS ── */}
      <Process />

      {/* ── 6. ABOUT ── */}
      <About />

      {/* ── 7. TESTIMONIALS ── */}
      <Testimonials />

      {/* ── 8. SERVICE AREA ── */}
      <ServiceArea />

      {/* ── 9. CTA SECTION ── */}
      <CTASection />

      {/* ── 10. GALLERY ── */}
      <Gallery />

      {/* ── 11. SPECIAL OFFER / PAGE CTA ── */}
      <section className="py-24 bg-[#C9A961]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready for Your Perfect Fit?
          </h2>
          <p className="text-xl text-white/85 mb-10">
            Call us today or request a nationwide house call. Our master tailors are ready to craft something extraordinary for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:866-857-8487"
              className="inline-flex items-center justify-center bg-white text-[#C9A961] px-10 py-4 rounded-full text-lg font-bold hover:bg-gray-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              866-857-8487
            </a>
            <Link
              to="/locations"
              className="inline-flex items-center justify-center border-2 border-white text-white px-10 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-[#C9A961] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Find Your Nearest Consultant
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* ── 12. FAQ ── */}
      <ServicesFAQ />

      {/* ── 13. CONSULTATION FORM ── */}
      <ConsultationForm />

      <Footer />
    </div>
  );
}
